import Field from "@/components/Field";
import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";
import ProjectSelect from "@/components/ProjectSelect";
import SaveButton from "@/components/SaveButton";
import TimeInput from "@/components/TimeInput";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PATH_PROJECT_CONTRACTOR_VISIT } from "@/constants";
import { isRequired } from "@/lib/form";
import { FilterEntry, FilterType } from "@/lib/filters";
import useAccount from "@/modules/account/hooks/useAccount";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import ProjectStoreSelect from "@/modules/projects/components/ProjectStoreSelect";
import ContractorVisitsSelect from "@/modules/projects/components/contractors-visits/components/ContractorVisitsSelect";
import { useCreateContractorVisitsMutation } from "@/modules/projects/components/contractors-visits/hooks/useCreateContractorVisitsMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useMemo } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useDependentFieldsReset } from "../hooks/useDependentFieldsReset";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import ProjectRoleSelect from "../../ProjectRoleSelect";
import { CreateContractorVisitFormSchema } from "../schema";
import { CreateContractorVisitFormType } from "../types";
import { createContractorVisit } from "@/schemas/contractorVisit";
import { PROJECT_DEPENDENCY_ROLE } from "@/schemas/commonFields";
import { SYSTEM_ROLE_CODE } from "@/schemas/user";
import { useCheckIsProjectLocked } from "@/modules/projects/hooks/useCheckIsProjectLocked";
import { PredefinedSettlementTypeSelect } from "@/modules/projects/components/PredefinedSettlementTypeSelect";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { useIsVisitEditableForRegionalRepresentative } from "../hooks/useIsVisitEditableForRegionalRepresentative";
import { NumberInput } from "@/components/ui/NumberInput";

const defaultValues = {
  date: "",
  suggested_start_time: "",
  suggested_end_time: "",
  pr_planned_duration: "",
};

export const ContractorVisitsCreator = memo(() => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const { can_create } = useModulePermissions();
  const isRegionalRepresentative =
    account?.system_role_code === SYSTEM_ROLE_CODE.REGIONAL_REPRESENTATIVE;
  const navigate = useNavigate();
  const createMutation = useCreateContractorVisitsMutation();

  const form = useForm<CreateContractorVisitFormType>({
    resolver: zodResolver(CreateContractorVisitFormSchema),
    defaultValues,
    disabled: !can_create,
  });

  const handleSubmit: SubmitHandler<CreateContractorVisitFormType> =
    useCallback(
      async (data) => {
        const payload = createContractorVisit.parse(data);
        const result = await createMutation.mutateAsync(payload);
        navigate(
          generatePath(PATH_PROJECT_CONTRACTOR_VISIT, {
            contractorVisitId: result.id,
          }),
        );
        return result;
      },
      [createMutation, navigate],
    );

  const breadcrumbItems = useMemo(
    () => [
      {
        label: t("breadcrumb.home"),
        href: "/",
      },
      {
        label: t("projectContractorVisits.title.multiple"),
        href: "/projects/contractor-visits",
      },
      {
        label: t("projectContractorVisits.breadcrumb.create"),
      },
    ],
    [t],
  );

  const projectId = useWatch({
    control: form.control,
    name: "project_id",
  });

  const storeId = useWatch({
    control: form.control,
    name: "store_id",
  });

  const projectRoleId = useWatch({
    control: form.control,
    name: "project_role_id",
  });

  const date = useWatch({
    control: form.control,
    name: "date",
  });

  const isProjectLocked = useCheckIsProjectLocked(projectId);
  const projectQuery = useProjectQuery(projectId);
  const canRegionalRepresentativeCreateVisit =
    useIsVisitEditableForRegionalRepresentative(
      projectQuery.data?.contractor_visits_editable_by_pr,
    );

  const projectFilters = useMemo(() => {
    const filters: FilterEntry[] = [];

    if (isRegionalRepresentative) {
      filters.push({
        id: "contractor_visits_editable_by_pr",
        value: { value: "true", filter: FilterType.EqualsString },
      });
    }
    return filters.length > 0 ? filters : undefined;
  }, [isRegionalRepresentative]);

  useDependentFieldsReset(form, projectId, storeId, projectRoleId, date);

  const queryParams = useMemo(() => {
    return {
      project_role_id: projectRoleId,
      store_id: storeId,
      date: date,
    };
  }, [projectRoleId, storeId, date]);

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>
              {t("projectContractorVisits.createForm.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              name="project_id"
              label={t(`projectContractorVisits.fields.project_name`)}
              required={isRequired(
                CreateContractorVisitFormSchema.shape.project_id,
              )}
              render={({ field }) => (
                <ProjectSelect
                  className="w-full"
                  projectDependsOn={PROJECT_DEPENDENCY_ROLE}
                  filters={projectFilters}
                  {...field}
                />
              )}
            />
            <Field
              name="store_id"
              label={t(`projectContractorVisits.fields.store_name`)}
              required={isRequired(
                CreateContractorVisitFormSchema.shape.store_id,
              )}
              render={({ field }) => (
                <ProjectStoreSelect
                  key={projectId}
                  projectId={projectId}
                  className="w-full"
                  {...field}
                  disabled={projectId === undefined}
                />
              )}
            />
            <Field
              name="project_role_id"
              label={t(`projectContractorVisits.fields.project_role_id`)}
              required={isRequired(
                CreateContractorVisitFormSchema.shape.project_role_id,
              )}
              render={({ field }) => (
                <ProjectRoleSelect
                  key={projectId}
                  projectId={projectId}
                  className="w-full"
                  {...field}
                  disabled={projectId === undefined}
                />
              )}
            />
            <Field
              name="settlement_type"
              label={t(`projectContractorVisits.fields.settlement_type`)}
              required={isRequired(
                CreateContractorVisitFormSchema.shape.settlement_type,
              )}
              render={({ field }) => (
                <PredefinedSettlementTypeSelect
                  {...field}
                  projectRoleId={projectRoleId}
                  storeId={storeId}
                />
              )}
            />
            <Field
              name="date"
              label={t(`projectContractorVisits.fields.date`)}
              required={isRequired(CreateContractorVisitFormSchema.shape.date)}
              render={({ field }) => (
                <Input
                  {...field}
                  type="date"
                  className="w-full"
                  disabled={projectId === undefined || storeId === undefined}
                />
              )}
            />
            <Field
              name="contractor_id"
              label={t(`projectContractorVisits.fields.contractor_id`)}
              required={isRequired(
                CreateContractorVisitFormSchema.shape.contractor_id,
              )}
              render={({ field }) => (
                <ContractorVisitsSelect
                  key={`${projectRoleId}-${storeId}-${date}`}
                  className="w-full"
                  {...field}
                  queryParams={queryParams}
                  disabled={
                    projectId === undefined ||
                    storeId === undefined ||
                    date === undefined ||
                    date === ""
                  }
                />
              )}
            />
            <Field
              name="suggested_start_time"
              label={t("projectContractorVisits.fields.suggested_start_time")}
              render={({ field }) => (
                <TimeInput {...field} disabled={createMutation.isPending} />
              )}
            />
            <Field
              name="suggested_end_time"
              label={t("projectContractorVisits.fields.suggested_end_time")}
              render={({ field }) => (
                <TimeInput {...field} disabled={createMutation.isPending} />
              )}
            />
            <Field
              name="pr_planned_duration"
              label={t("projectContractorVisits.fields.pr_planned_duration")}
              render={({ field }) => (
                <NumberInput {...field} disabled={createMutation.isPending} />
              )}
            />
          </CardContent>
        </Card>
        <footer className="flex flex-col sm:flex-row gap-3 justify-end">
          <SaveButton
            disabled={isProjectLocked || !canRegionalRepresentativeCreateVisit}
          />
        </footer>
      </Form>
    </>
  );
});
